// BLOCK S7 1 — ONE code-shipped guide registry. Platform content, identical for
// every company (not tenant data, no DB). The Help panel, Jump-to (⌘K) and the
// chat assistant all read THIS file — guide text lives nowhere else.
//
// Each step: title, detail (plain words), target {route, section} for
// "Take me there", spotlight (an existing data-testid) for "Show me", and
// doneWhen (a named server check, computed by GET /api/guides/{id}/progress)
// OR manual:true. Step order MUST match backend GUIDE_CHECKS in routes/guides.py.
//
// route may contain ':eventId' — the How-To card fills it from the active event.

export const GUIDES = [
  {
    id: 'outside-event',
    title: 'Pick up an event that started outside STAFFED',
    shortTitle: 'Outside event',
    minutes: 6,
    pages: [{ routePrefix: '/event/' }],
    keywords: ['outside', 'already booked', 'existing event', 'zelle', 'retainer', 'manual', 'migrate', 'took over', 'legacy'],
    steps: [
      {
        title: 'Skip what was done elsewhere',
        detail: 'On Proposal, Contract and Planning, choose Skip for anything already handled outside STAFFED.',
        target: { route: '/event/:eventId', section: 'proposal' },
        spotlight: '[data-testid="module-skip-control-proposal"]',
        doneWhen: 'proposalAndContractSkipped',
      },
      {
        title: 'Create the payment schedule',
        detail: 'Payments → Set up payments → enter the total and the retainer → Create Payment Schedule.',
        target: { route: '/event/:eventId', section: 'payments' },
        spotlight: '[data-testid="create-schedule-btn"]',
        doneWhen: 'hasPaymentLine',
      },
      {
        title: 'Record what they already paid',
        detail: 'On the Retainer line choose Mark paid, pick how it was paid, the real date, and untick Send receipt if they already have one.',
        target: { route: '/event/:eventId', section: 'payments' },
        spotlight: '[data-testid="module-tab-payments"]',
        doneWhen: 'retainerPaid',
      },
      {
        title: 'Check the balance due date',
        detail: 'Click the date on the balance line to match their agreement.',
        target: { route: '/event/:eventId', section: 'payments' },
        spotlight: '[data-testid="due-date-edit-balance"]',
        manual: true,
      },
      {
        title: 'Send the client portal invite',
        detail: 'Text the client first so the email isn\u2019t a surprise, then press Send Client Portal Invite.',
        target: { route: '/event/:eventId', section: 'payments' },
        spotlight: '[data-testid="workspace-send-portal-invite-btn"]',
        doneWhen: 'portalInviteSent',
      },
      {
        title: 'Let STAFFED collect the rest',
        detail: 'Card payments record themselves. For Zelle or check, use Mark paid on the balance line.',
        target: { route: '/event/:eventId', section: 'payments' },
        spotlight: '[data-testid="module-tab-payments"]',
        doneWhen: 'balancePaid',
      },
    ],
  },
  {
    id: 'record-manual-payment',
    title: 'Record a Zelle, check or cash payment',
    shortTitle: 'Record payment',
    minutes: 3,
    pages: [{ routePrefix: '/event/' }],
    keywords: ['zelle', 'check', 'cheque', 'cash', 'mark paid', 'record payment', 'manual payment', 'venmo'],
    steps: [
      {
        title: 'Open the Payments tab',
        detail: 'Open the event and choose the Payments tab.',
        target: { route: '/event/:eventId', section: 'payments' },
        spotlight: '[data-testid="module-tab-payments"]',
        manual: true,
      },
      {
        title: 'Find the line and choose Mark paid',
        detail: 'On the retainer or balance line, press Mark paid.',
        target: { route: '/event/:eventId', section: 'payments' },
        spotlight: '[data-testid="module-tab-payments"]',
        doneWhen: 'hasPaymentLine',
      },
      {
        title: 'Enter how they paid',
        detail: 'Pick Zelle, check or cash, set the real date, and untick Send receipt if they already have one.',
        target: { route: '/event/:eventId', section: 'payments' },
        spotlight: '[data-testid="module-tab-payments"]',
        doneWhen: 'balancePaid',
      },
    ],
  },
  {
    id: 'send-proposal-contract',
    title: 'Send a proposal and contract',
    shortTitle: 'Proposal & contract',
    minutes: 5,
    pages: [{ routePrefix: '/event/' }],
    keywords: ['proposal', 'contract', 'send', 'quote', 'sign', 'esign', 'agreement'],
    steps: [
      {
        title: 'Build and send the proposal',
        detail: 'On the Proposal tab, set the packages and add-ons, then press Send to client.',
        target: { route: '/event/:eventId', section: 'proposal' },
        spotlight: '[data-testid="module-tab-proposal"]',
        doneWhen: 'proposalSent',
      },
      {
        title: 'Send the contract to sign',
        detail: 'On the Contract tab, review the terms and total, then send it for the client to sign.',
        target: { route: '/event/:eventId', section: 'contract' },
        spotlight: '[data-testid="module-tab-contract"]',
        doneWhen: 'contractSigned',
      },
    ],
  },
  {
    id: 'staff-an-event',
    title: 'Staff an event',
    shortTitle: 'Staff event',
    minutes: 5,
    pages: [{ routePrefix: '/event/' }],
    keywords: ['staff', 'assign', 'crew', 'shift', 'roster', 'team', 'schedule staff'],
    steps: [
      {
        title: 'Confirm the booking is settled',
        detail: 'Staffing opens once the event is booked with a payment schedule in place.',
        target: { route: '/event/:eventId', section: 'payments' },
        spotlight: '[data-testid="module-tab-payments"]',
        doneWhen: 'hasPaymentLine',
      },
      {
        title: 'Assign your team',
        detail: 'On the Staffing tab, add the roles you need and assign staff to each shift.',
        target: { route: '/event/:eventId', section: 'staffing' },
        spotlight: '[data-testid="module-tab-staffing"]',
        doneWhen: 'staffAssigned',
      },
      {
        title: 'Send the shift invites',
        detail: 'Send each assignment so your team can accept it from their phone.',
        target: { route: '/event/:eventId', section: 'staffing' },
        spotlight: '[data-testid="module-tab-staffing"]',
        manual: true,
      },
    ],
  },
  {
    id: 'refund-a-payment',
    title: 'Refund or correct a payment',
    shortTitle: 'Refund payment',
    minutes: 3,
    pages: [{ routePrefix: '/event/' }],
    keywords: ['refund', 'correct', 'mistake', 'reverse', 'wrong amount', 'undo payment'],
    steps: [
      {
        title: 'Open the Payments tab',
        detail: 'Open the event and choose the Payments tab where the line lives.',
        target: { route: '/event/:eventId', section: 'payments' },
        spotlight: '[data-testid="module-tab-payments"]',
        doneWhen: 'hasPaymentLine',
      },
      {
        title: 'Refund a card payment',
        detail: 'For a card payment, open the line and choose Record refund, then enter the amount.',
        target: { route: '/event/:eventId', section: 'payments' },
        spotlight: '[data-testid="module-tab-payments"]',
        manual: true,
      },
      {
        title: 'Correct a manual payment',
        detail: 'For Zelle, check or cash, reset the line to pending or edit the amount to match reality.',
        target: { route: '/event/:eventId', section: 'payments' },
        spotlight: '[data-testid="module-tab-payments"]',
        manual: true,
      },
    ],
  },
  {
    id: 'close-out-event',
    title: 'Close out an event after the night',
    shortTitle: 'Close out',
    minutes: 4,
    pages: [{ routePrefix: '/control-center' }],
    keywords: ['close out', 'after the event', 'wrap up', 'final', 'finish event', 'command deck'],
    steps: [
      {
        title: 'Open the Command Deck',
        detail: 'Go to the Command Deck to work through the events that just happened.',
        target: { route: '/control-center' },
        spotlight: '[data-testid="page-header"]',
        manual: true,
      },
      {
        title: 'Collect anything still owed',
        detail: 'Make sure the balance is paid in full — record any last Zelle or check on the Payments tab.',
        target: { route: '/event/:eventId', section: 'payments' },
        spotlight: '[data-testid="module-tab-payments"]',
        doneWhen: 'balancePaid',
      },
      {
        title: 'Wrap up the event',
        detail: 'Confirm the final details and mark the event closed for the night.',
        target: { route: '/control-center' },
        spotlight: '[data-testid="page-header"]',
        manual: true,
      },
    ],
  },
  {
    id: 'package-contents',
    title: 'Change what a package includes or upgrade prices',
    shortTitle: 'Packages',
    minutes: 5,
    pages: [{ routePrefix: '/templates' }, { routePrefix: '/settings/package-contents' }],
    keywords: ['package', 'add-on', 'upgrade', 'pricing', 'includes', 'contents', 'templates', 'big structure'],
    steps: [
      {
        title: 'Open Templates → Add-ons',
        detail: 'Go to Templates and scroll to the list where you keep every item you offer.',
        target: { route: '/templates', section: 'addons' },
        spotlight: '[data-testid="addons-editor"]',
        manual: true,
      },
      {
        title: 'Set upgrade pricing',
        detail: 'In the Upgrade pricing card, set the fee percent and the two minimum fees, then Save.',
        target: { route: '/templates', section: 'addons' },
        spotlight: '[data-testid="upgrade-pricing-card"]',
        manual: true,
      },
      {
        title: 'Set what each package adds',
        detail: 'Open Package Contents. Each package builds on the tier below, so you only add what\u2019s extra. Use \u201cReplaces an included item\u201d when something is an upgrade, so a client is never charged twice.',
        target: { route: '/settings/package-contents' },
        spotlight: '[data-testid="package-contents-editor"]',
        manual: true,
      },
    ],
  },
  {
    id: 'embed-form',
    title: 'Put the enquiry form on your website',
    shortTitle: 'Embed form',
    minutes: 4,
    pages: [{ routePrefix: '/settings/embed-code' }],
    keywords: ['embed', 'website', 'form', 'enquiry', 'inquiry', 'lead form', 'code', 'snippet'],
    steps: [
      {
        title: 'Copy the embed code',
        detail: 'Go to Settings → Embed Code and copy the snippet for your website.',
        target: { route: '/settings/embed-code' },
        spotlight: '[data-testid="page-header"]',
        manual: true,
      },
      {
        title: 'Paste it on your site and test',
        detail: 'Paste it where you want the form, publish, and submit a test enquiry — it appears as your first website lead.',
        target: { route: '/settings/embed-code' },
        spotlight: '[data-testid="page-header"]',
        manual: true,
      },
    ],
  },
];

export const getGuide = (id) => GUIDES.find((g) => g.id === id) || null;

const routeMatches = (pages, pathname) =>
  (pages || []).some((p) => p.routePrefix && pathname.startsWith(p.routePrefix));

// Guides whose pages match the current route (used by the Help panel's
// "Guides for this page").
export const guidesForRoute = (pathname) =>
  GUIDES.filter((g) => routeMatches(g.pages, pathname));

// Registry search by title + keywords (Help panel + Jump-to + assistant match).
export const searchGuides = (query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return GUIDES;
  const terms = q.split(/\s+/);
  return GUIDES.filter((g) => {
    const hay = `${g.title} ${g.shortTitle} ${(g.keywords || []).join(' ')}`.toLowerCase();
    return terms.every((t) => hay.includes(t));
  });
};

// Best single guide match for a free-text assistant answer/question.
export const matchGuide = (text) => {
  const q = (text || '').toLowerCase();
  if (!q) return null;
  let best = null;
  let bestScore = 0;
  GUIDES.forEach((g) => {
    let score = 0;
    if (q.includes(g.title.toLowerCase())) score += 5;
    (g.keywords || []).forEach((k) => { if (q.includes(k.toLowerCase())) score += 1; });
    if (score > bestScore) { bestScore = score; best = g; }
  });
  return bestScore >= 2 ? best : null;
};
